import { router, useFocusEffect } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Skeleton } from '@/components/Skeleton';
import { SocialCreateBanner } from '@/components/social/SocialCreateBanner';
import { SocialDealCard } from '@/components/social/SocialDealCard';
import { SocialFilterChips, type SocialFilter } from '@/components/social/SocialFilterChips';
import { SocialSection } from '@/components/social/SocialSection';
import { colors, spacing } from '@/constants/theme';
import { socialStage } from '@/lib/socialProgress';
import type { SocialDealStage } from '@/types/socialDeal';
import { authStore } from '@/stores/auth.store';
import { dealsStore } from '@/stores/deals.store';

/**
 * The Create tab (Figma "Create" frame): MarketPryce Social's deals, for
 * users with `social_enabled` (the tab bar swaps it in for Leaderboard; see _layout). Same deals
 * and actions the Deals tab loads, narrowed to `deal.social`; a card opens
 * the deal page.
 */
const CreateTab = observer(() => {
  const [filter, setFilter] = useState<SocialFilter>('all');

  useFocusEffect(
    useCallback(() => {
      void dealsStore.ensureLoaded();
      void dealsStore.refreshList();
    }, []),
  );

  const onRefresh = useCallback(() => {
    void dealsStore.pullRefreshList();
  }, []);

  const copy = authStore.user?.social_create_copy;
  const social = dealsStore.deals.filter((d) => d.social);
  const stage = FILTER_STAGE[filter];
  const shown = stage ? social.filter((d) => socialStage(dealsStore.actionFor(d.id)) === stage) : social;
  const showLoading = dealsStore.loading && social.length === 0;
  const showError = !!dealsStore.error && social.length === 0;
  const showEmpty = !showLoading && !showError && social.length === 0;

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={
          <RefreshControl refreshing={dealsStore.pullRefreshingList} onRefresh={onRefresh} tintColor={colors.deepGreen} />
        }
      >
        {copy ? <SocialCreateBanner copy={copy} testID="create-banner" /> : null}

        {showLoading && (
          <View style={styles.list} testID="create-skeleton">
            <Skeleton height={300} radius="lg" />
            <Skeleton height={300} radius="lg" />
          </View>
        )}

        {showError && (
          <ErrorState body={dealsStore.error ?? "Couldn't load deals."} onRetry={() => dealsStore.ensureLoaded()} />
        )}

        {showEmpty && (
          <EmptyState title="No deals right now" body="New brand deals show up here as soon as they’re live." testID="create-empty" />
        )}

        {social.length > 0 && (
          <SocialSection title="Deals for You" testID="create-deals">
            <SocialFilterChips value={filter} onChange={setFilter} testID="create-filter" />
            {shown.length === 0 ? (
              <EmptyState bare {...EMPTY_COPY[filter]} testID={`create-empty-${filter}`} />
            ) : (
              <View style={styles.list}>
                {shown.map((deal) => (
                  <SocialDealCard
                    key={deal.id}
                    deal={deal}
                    action={dealsStore.actionFor(deal.id)}
                    onPress={() => router.push(`/deals/${deal.slug}`)}
                    testID={`create-deal-${deal.slug}`}
                  />
                ))}
              </View>
            )}
          </SocialSection>
        )}
      </ScrollView>
    </SafeAreaView>
  );
});

export default CreateTab;

/** The stage each filter keeps; All keeps every deal. */
const FILTER_STAGE: Record<SocialFilter, SocialDealStage | null> = {
  all: null,
  in_progress: 'under_way',
  completed: 'over',
};

/** What a filter with no deals says, so the list never goes blank. */
const EMPTY_COPY: Record<SocialFilter, { title: string; body: string }> = {
  all: { title: 'No deals right now', body: 'New brand deals show up here as soon as they’re live.' },
  in_progress: { title: 'Nothing in progress', body: 'Deals you’ve started show up here.' },
  completed: { title: 'Nothing completed yet', body: 'Deals you’ve finished show up here.' },
};

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  scroll: { padding: spacing.lg, gap: spacing.lg },
  list: { gap: spacing.base },
});
